import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import { shopifyAppBridge } from '../App';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import usePagination from '../hooks/usePagination';
import useSearch from '../hooks/useSearch';
import PaginationFooter from './shared/PaginationFooter';
import EmptyState from './shared/EmptyState';
import ImageRow from './shared/ImageRow';
import OptimizationTypeFilter from './shared/OptimizationTypeFilter';
import CompareModal from './shared/CompareModal';
import CreditsWidget from './shared/CreditsWidget';

function OptimizedImagesManagement({ onJobStarted, onJobComplete, hideCredits, onGoToUnoptimized }) {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [products, setProducts] = useState([]);
  
  const shopDomain = (() => {
    if (!shopifyAppBridge?.config?.shop) return null;
    const shop = shopifyAppBridge.config.shop;
    return shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;
  })();

  const [selectedImages, setSelectedImages] = useState(new Set());
  const [selectAllStore, setSelectAllStore] = useState(false);
  const [allListingImageIds, setAllListingImageIds] = useState(new Set());
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [fetching, setFetching] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [reverting, setReverting] = useState(null);
  const [reoptimizing, setReoptimizing] = useState(null);
  const [optimizationTypeFilter, setOptimizationTypeFilter] = useState('all');
  const [optimizationTypes, setOptimizationTypes] = useState([]);
  const typesFetchedRef = useRef(false);
  
  const [pageSize] = useState(10);
  const { currentPage, pageInfo, updatePageInfo, goToPage, goToNextPage, goToPreviousPage, resetToFirstPage } = usePagination({ pageSize: 10 });
  const { searchQuery, debouncedSearchQuery, handleSearchChange, clearSearch } = useSearch(500, () => resetToFirstPage());

  const authenticatedFetchRef = useRef(authenticatedFetch);
  useEffect(() => { authenticatedFetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const currentFetchRef = useRef({ page: null, search: null, optimizationType: null });
  
  const fetchOptimizedImages = useCallback(async (page = 1, search = '', optimizationType = 'all') => {
    currentFetchRef.current = { page, search, optimizationType };
    if (search) setSearching(true); else setFetching(true);
    setError(null);

    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const optimizationTypeParam = optimizationType && optimizationType !== 'all' ? `&optimizationType=${encodeURIComponent(optimizationType)}` : '';
      const url = `/api/optimized-images?page=${page}&pageSize=${pageSize}${searchParam}${optimizationTypeParam}`;
      const response = await authenticatedFetchRef.current(url, { method: 'POST' });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Failed to fetch optimized images');
      if (currentFetchRef.current.page !== page || currentFetchRef.current.search !== search || currentFetchRef.current.optimizationType !== optimizationType) return;

      const receivedProducts = data.products || [];
      if (receivedProducts.length === 0 && page > 1 && data.pageInfo?.totalPages >= 1) {
        goToPage(1);
        return;
      }

      setProducts(receivedProducts);
      if (data.pageInfo) updatePageInfo(data.pageInfo);
      if (!selectAllStore) setSelectedImages(new Set());
    } catch (err) {
      setError(err.message || 'Failed to fetch optimized images');
    } finally {
      setFetching(false);
      setSearching(false);
    }
  }, [pageSize, selectAllStore, goToPage, updatePageInfo]);

  useEffect(() => {
    if (typesFetchedRef.current) return;
    async function fetchTypes() {
      typesFetchedRef.current = true;
      try {
        const response = await authenticatedFetchRef.current('/api/optimization-types');
        const data = await response.json();
        if (data.success && data.types) setOptimizationTypes(data.types);
      } catch (error) {
        console.error('Error fetching optimization types:', error);
      }
    }
    fetchTypes();
  }, []);

  useEffect(() => {
    fetchOptimizedImages(currentPage, debouncedSearchQuery, optimizationTypeFilter);
  }, [currentPage, debouncedSearchQuery, optimizationTypeFilter, fetchOptimizedImages]);

  const getAllImageIds = useCallback((productsList) => {
    const allIds = new Set();
    productsList.forEach(product => product.images.forEach(img => allIds.add(img.id)));
    return allIds;
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectAllStore) {
      setSelectAllStore(false);
      const allImageIds = getAllImageIds(products);
      setSelectedImages(new Set(allImageIds));
      return;
    }
    const allImageIds = getAllImageIds(products);
    const allPageSelected = selectedImages.size === allImageIds.size && allImageIds.size > 0;
    if (allPageSelected) setSelectedImages(new Set()); else setSelectedImages(new Set(allImageIds));
  }, [selectedImages.size, products, getAllImageIds, selectAllStore]);

  const fetchAllImagesFromListing = useCallback(async (search = '', optimizationType = 'all') => {
    const allImageIds = new Set();
    let currentPageNum = 1;
    let hasMorePages = true;
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const optimizationTypeParam = optimizationType && optimizationType !== 'all' ? `&optimizationType=${encodeURIComponent(optimizationType)}` : '';

    try {
      while (hasMorePages) {
        const url = `/api/optimized-images?page=${currentPageNum}&pageSize=${pageSize}${searchParam}${optimizationTypeParam}`;
        const response = await authenticatedFetchRef.current(url, { method: 'POST' });
        if (!response.ok) throw new Error('Failed to fetch images');
        const data = await response.json();
        const products = data.products || [];
        products.forEach(product => product.images.forEach(img => allImageIds.add(img.id)));
        if (data.pageInfo && data.pageInfo.hasNextPage) currentPageNum++; else hasMorePages = false;
      }
      return allImageIds;
    } catch (err) {
      console.error('❌ Error fetching all images', err);
      throw err;
    }
  }, [pageSize]);

  const handleSelectAllStore = useCallback(async () => {
    setSelectAllStore(true);
    setError(null);
    if (searchQuery.trim() !== '') clearSearch();
    if (optimizationTypeFilter !== 'all') setOptimizationTypeFilter('all');
    try {
      const allImageIds = await fetchAllImagesFromListing('', 'all');
      setAllListingImageIds(allImageIds);
      setSelectedImages(new Set());
    } catch (err) {
      setError('Failed to select all images from listing');
      setSelectAllStore(false);
    }
  }, [searchQuery, optimizationTypeFilter, clearSearch, fetchAllImagesFromListing]);

  const handleUnselectAll = useCallback(() => {
    setSelectAllStore(false);
    setSelectedImages(new Set());
    setAllListingImageIds(new Set());
  }, []);

  const handleUndoSelectAllStore = useCallback(() => {
    setSelectAllStore(false);
    const allImageIds = getAllImageIds(products);
    setSelectedImages(new Set(allImageIds));
  }, [products, getAllImageIds]);

  const handleProductToggle = useCallback((productId, productImages) => {
    setSelectedImages(prev => {
      const productImageIds = new Set(productImages.map(img => img.id));
      const allSelected = productImageIds.size > 0 && Array.from(productImageIds).every(id => prev.has(id));
      const newSelected = new Set(prev);
      if (allSelected) productImageIds.forEach(id => newSelected.delete(id));
      else productImageIds.forEach(id => newSelected.add(id));
      return newSelected;
    });
  }, []);

  const handleToggleExpand = useCallback((productId) => {
    setExpandedProducts(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(productId)) newExpanded.delete(productId); else newExpanded.add(productId);
      return newExpanded;
    });
  }, []);

  const getProductCheckboxState = useCallback((product) => {
    if (selectAllStore) return { checked: true, indeterminate: false };
    const productImageIds = product.images.map(img => img.id);
    if (productImageIds.length === 0) return { checked: false, indeterminate: false };
    const selectedCount = productImageIds.filter(id => selectedImages.has(id)).length;
    const allSelected = selectedCount === productImageIds.length;
    const someSelected = selectedCount > 0 && selectedCount < productImageIds.length;
    return { checked: allSelected, indeterminate: someSelected };
  }, [selectedImages, selectAllStore]);

  const handleRevert = useCallback(async () => {
    if (selectedImages.size === 0 && !selectAllStore) {
      setError('Please select at least one image to revert');
      return;
    }
    setReverting('global');
    setError(null);
    try {
      const payload = selectAllStore ? { imageIds: Array.from(allListingImageIds) } : { imageIds: Array.from(selectedImages) };
      const response = await authenticatedFetchRef.current('/api/revert-images', { method: 'POST', body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to revert images');
      showSuccessToast(data.message || `selected images reverted successfully`);
      setSelectAllStore(false);
      setSelectedImages(new Set());
      setAllListingImageIds(new Set());
      await fetchOptimizedImages(currentPage, debouncedSearchQuery, optimizationTypeFilter);
      if (onJobStarted) onJobStarted();
    } catch (err) {
      showErrorToast(err.message || 'Failed to revert images');
    } finally {
      setReverting(null);
    }
  }, [selectedImages.size, selectAllStore, allListingImageIds, currentPage, debouncedSearchQuery, optimizationTypeFilter, fetchOptimizedImages, onJobStarted]);

  const handleProductRevertClick = useCallback(async (product, selectedImageIds) => {
    if (selectedImageIds.length === 0) return;
    setReverting(product.productId);
    setError(null);
    try {
      const response = await authenticatedFetchRef.current('/api/revert-images', { method: 'POST', body: JSON.stringify({ imageIds: selectedImageIds }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to revert images');
      showSuccessToast(data.message || `selected images reverted successfully`);
      setSelectedImages(prev => {
        const newSelected = new Set(prev);
        selectedImageIds.forEach(id => newSelected.delete(id));
        return newSelected;
      });
      await fetchOptimizedImages(currentPage, debouncedSearchQuery, optimizationTypeFilter);
      if (onJobStarted) onJobStarted();
    } catch (err) {
      showErrorToast(err.message || 'Failed to revert images');
    } finally {
      setReverting(null);
    }
  }, [currentPage, debouncedSearchQuery, optimizationTypeFilter, fetchOptimizedImages, onJobStarted]);

  const handleProductReoptimize = useCallback(async (product, optimizationType = 'regular') => {
    setReoptimizing(product.productId);
    setError(null);
    try {
      const productSelectedImages = product.images.filter(img => selectedImages.has(img.id)).map(img => img.id);
      if (productSelectedImages.length === 0) throw new Error('No images selected for re-optimization');

      // Pre-check credits so the job never starts if there aren't enough
      const balRes = await authenticatedFetchRef.current('/api/credits/balance');
      const balData = await balRes.json();
      if (balData.success) {
        const remaining = balData.data?.remainingCredits ?? 0;
        if (remaining < productSelectedImages.length) {
          showErrorToast(remaining === 0
            ? `No credits remaining. Please purchase more credits to continue.`
            : `Credit limit reached. You selected ${productSelectedImages.length} images but only have ${remaining} credits. Please select fewer images or purchase more credits.`
          );
          setReoptimizing(null);
          return;
        }
      }

      const payload = { optimizationType, isReoptimize: true, imageCount: productSelectedImages.length, imageIds: productSelectedImages };
      const response = await authenticatedFetchRef.current('/api/optimize', { method: 'POST', body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'MAX_JOBS_RUNNING') {
          showErrorToast(data.message || 'Maximum 2 optimization jobs can run simultaneously.');
          setReoptimizing(null);
          return;
        }
        throw new Error(data.message || 'Failed to start re-optimization');
      }
      setSelectedImages(new Set());
      setSelectAllStore(false);
      setAllListingImageIds(new Set());
      if (onJobStarted && data.jobId) onJobStarted(data.jobId);
      if (onJobComplete) onJobComplete();
      showSuccessToast('Re-optimization job started successfully');
      navigate('/', { state: { pendingJobId: data.jobId || null, wantScrollToJobs: true } });
    } catch (err) {
      setError(err.message || 'Failed to re-optimize product');
    } finally {
      setReoptimizing(null);
    }
  }, [selectedImages, onJobStarted, onJobComplete, navigate]);

  const handleGlobalReoptimize = useCallback(async (optimizationType = 'regular') => {
    setReoptimizing('global');
    setError(null);
    try {
      const payload = { optimizationType, isReoptimize: true };
      let imageCount = 0;
      if (selectAllStore) {
        const allImageIds = Array.from(allListingImageIds);
        payload.imageIds = allImageIds;
        payload.imageCount = allImageIds.length;
        imageCount = allImageIds.length;
      } else if (selectedImages.size > 0) {
        const selectedImageIds = Array.from(selectedImages);
        payload.imageIds = selectedImageIds;
        payload.imageCount = selectedImageIds.length;
        imageCount = selectedImageIds.length;
      } else {
        throw new Error('No images selected for re-optimization');
      }

      // Pre-check credits before calling API
      const balRes = await authenticatedFetchRef.current('/api/credits/balance');
      const balData = await balRes.json();
      if (balData.success) {
        const remaining = balData.data?.remainingCredits ?? 0;
        if (remaining < imageCount) {
          showErrorToast(remaining === 0
            ? `No credits remaining. Please purchase more credits to continue.`
            : `Credit limit reached. You selected ${imageCount} images but only have ${remaining} credits. Please select fewer images or purchase more credits.`
          );
          setReoptimizing(null);
          return;
        }
      }

      const response = await authenticatedFetchRef.current('/api/optimize', { method: 'POST', body: JSON.stringify(payload) });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'MAX_JOBS_RUNNING') {
          showErrorToast(data.message || 'Maximum 2 optimization jobs can run simultaneously.');
          setReoptimizing(null);
          return;
        }
        throw new Error(data.message || 'Failed to start re-optimization');
      }
      setSelectedImages(new Set());
      setSelectAllStore(false);
      if (onJobStarted && data.jobId) onJobStarted(data.jobId);
      if (onJobComplete) onJobComplete();
      showSuccessToast('Re-optimization job started successfully');
      navigate('/', { state: { pendingJobId: data.jobId || null, wantScrollToJobs: true } });
    } catch (err) {
      setError(err.message || 'Failed to re-optimize images');
    } finally {
      setReoptimizing(null);
    }
  }, [selectAllStore, allListingImageIds, selectedImages, onJobStarted, onJobComplete, navigate]);

  const [compareImage, setCompareImage] = useState(null);
  const handleOpenCompare = useCallback((image) => {
    if (!image?.s3OriginalImageUrl || !image?.shopifyImageUrl) return;
    setCompareImage({
      originalName: image.imageName,
      originalUrl: image.s3OriginalImageUrl,
      optimizedUrl: image.shopifyImageUrl,
      originalSizeKb: parseFloat(image.originalSize ?? 0),
      optimizedSizeKb: parseFloat(image.optimizedSize ?? 0),
      savingsPercent: image.savingsPercent,
    });
  }, []);

  const totalImageCount = products.reduce((sum, product) => sum + product.images.length, 0);
  const allSelectedState = totalImageCount > 0 && (selectAllStore || selectedImages.size === totalImageCount);
  const someSelectedState = !selectAllStore && selectedImages.size > 0 && selectedImages.size < totalImageCount;

  return (
    <>
      <s-section padding="none">
        <s-stack gap="small-200">
          {error && (
            <s-banner tone="critical" dismissible onDismiss={() => setError(null)}>
              <s-text>{error}</s-text>
            </s-banner>
          )}

          <div className="sticky-header">
            <s-stack paddingBlockStart="base" gap="small-200">
              {!hideCredits && (
              <s-stack direction="inline" justifyContent="end">
                <CreditsWidget onPurchase={() => navigate('/pricing')} />
              </s-stack>
              )}
              <s-grid gridTemplateColumns="1fr auto auto auto" gap="base" alignItems="center">
                <s-search-field
                  label="Search"
                  labelAccessibilityVisibility="exclusive"
                  placeholder={searchQuery.trim() ? "Search..." : "Filter..."}
                  value={searchQuery}
                  onInput={handleSearchChange}
                  disabled={selectAllStore}
                />
                <OptimizationTypeFilter
                  optimizationTypeFilter={optimizationTypeFilter}
                  onChange={(type) => { setOptimizationTypeFilter(type); resetToFirstPage(); }}
                  disabled={fetching || searching || selectAllStore}
                />
                <s-button onClick={() => { resetToFirstPage(); fetchOptimizedImages(1, debouncedSearchQuery, optimizationTypeFilter); }} disabled={fetching} variant="secondary">
                  Refresh
                </s-button>
                <s-stack direction="inline" gap="small-200" alignItems="center">
                  <s-button tone="critical" onClick={handleRevert} disabled={(selectedImages.size === 0 && !selectAllStore) || reverting === 'global'} loading={reverting === 'global'}>
                    Revert {selectAllStore ? 'All' : `(${selectedImages.size})`}
                  </s-button>
                  {(selectedImages.size > 0 || selectAllStore) && (
                    <>
                      <s-button variant="secondary" commandFor="global-reoptimize-menu" disabled={reoptimizing === 'global'}>
                        Re-optimize {selectAllStore ? 'All' : `(${selectedImages.size})`}
                      </s-button>
                      <s-menu id="global-reoptimize-menu" accessibilityLabel="Global reoptimize">
                        {optimizationTypes.map((type) => (
                          <s-button key={type.key} onClick={() => handleGlobalReoptimize(type.key)} disabled={reoptimizing === 'global'} loading={reoptimizing === 'global'}>
                            {type.title}
                          </s-button>
                        ))}
                      </s-menu>
                    </>
                  )}
                </s-stack>
              </s-grid>

              {(fetching || searching) && (
                <s-box border="base" borderRadius="base">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <s-box key={i} borderStyle="none none solid none" border="base" padding="base">
                      <s-grid gridTemplateColumns="auto auto 1fr auto" gap="base" alignItems="center">
                        <div className="shimmer-loader" style={{ height: 24, width: 24 }} />
                        <div className="shimmer-loader" style={{ height: 20, width: 20 }} />
                        <s-stack gap="small">
                          <div className="shimmer-loader" style={{ height: 18, width: 200 }} />
                          <div className="shimmer-loader" style={{ height: 14, width: 150 }} />
                        </s-stack>
                        <div className="shimmer-loader" style={{ height: 32, width: 80 }} />
                      </s-grid>
                    </s-box>
                  ))}
                </s-box>
              )}

              {!fetching && !searching && products.length > 0 && (
                <s-stack gap="small-200">
                  <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                      <s-checkbox
                        label={`Showing ${totalImageCount} ${totalImageCount === 1 ? 'image' : 'images'}`}
                        checked={allSelectedState || selectAllStore}
                        indeterminate={someSelectedState && !selectAllStore}
                        onInput={handleSelectAll}
                        disabled={selectAllStore}
                      />
                      {!selectAllStore && (selectedImages.size > 0 || allSelectedState) && debouncedSearchQuery.trim() === '' && optimizationTypeFilter === 'all' && (
                        <>
                          <s-button variant="secondary" size="small" commandFor="select-all-images-menu">
                            <s-text size="small">{selectedImages.size || totalImageCount} selected</s-text>
                          </s-button>
                          <s-menu id="select-all-images-menu" accessibilityLabel="Selection actions">
                            <s-button onClick={handleSelectAllStore}>Select all {pageInfo.totalProducts} in this listing</s-button>
                            <s-button onClick={handleUnselectAll}>Unselect all</s-button>
                          </s-menu>
                        </>
                      )}
                      {selectAllStore && (
                        <>
                          <s-button variant="secondary" size="small" commandFor="undo-select-all-images-menu">
                            <s-text size="small">All {pageInfo.totalProducts} in this listing selected</s-text>
                          </s-button>
                          <s-menu id="undo-select-all-images-menu" accessibilityLabel="Selection actions">
                            <s-button onClick={handleUndoSelectAllStore}>Undo select all</s-button>
                            <s-button onClick={handleUnselectAll}>Unselect all</s-button>
                          </s-menu>
                        </>
                      )}
                    </s-stack>
                  </s-grid>
                </s-stack>
              )}
            </s-stack>
          </div>

          {!fetching && !searching && products.length > 0 && (
            <div className="image-management-table">
              <div className="image-management-header">
                <span style={{ width: 32 }} />
                <span style={{ width: 20 }} />
                <s-text type="strong">Product</s-text>
                <s-text type="strong">Actions</s-text>
              </div>

              <div>
                {products.map((product) => {
                  const isExpanded = expandedProducts.has(product.productId);
                  const productCheckboxState = getProductCheckboxState(product);
                  const productImageCount = product.images.length;

                  return (
                    <div key={product.productId} className="image-management-product-row">
                      <div
                        className="clickable-row"
                        onClick={(e) => {
                          if (e.target.closest('s-checkbox,s-button,s-menu,input,button')) return;
                          handleToggleExpand(product.productId);
                        }}
                      >
                        <s-grid gridTemplateColumns="auto auto 1fr auto" gap="base" alignItems="center">
                          <s-button
                            variant="tertiary"
                            icon={isExpanded ? "chevron-down" : "chevron-right"}
                            onClick={(e) => { e.stopPropagation(); handleToggleExpand(product.productId); }}
                          />
                          <s-checkbox
                            checked={productCheckboxState.checked}
                            indeterminate={productCheckboxState.indeterminate}
                            onInput={(e) => { e.stopPropagation(); handleProductToggle(product.productId, product.images); }}
                            disabled={selectAllStore}
                          />
                          <s-stack direction="inline" gap="small" alignItems="center">
                            {product.featuredImageUrl ? (
                              <s-box border="base" borderRadius="base" overflow="hidden" inlineSize="40px" blockSize="40px" className="product-thumbnail-container">
                                <img src={product.featuredImageUrl} alt={product.productName} className="product-thumbnail-img" />
                              </s-box>
                            ) : (
                              <s-box inlineSize="40px" blockSize="40px" borderRadius="base" className="product-thumbnail-container">
                                <s-text tone="subdued" className="product-thumbnail-fallback">📦</s-text>
                              </s-box>
                            )}
                            <s-stack gap="small-100">
                              <s-heading>{product.productName}</s-heading>
                              <s-text color="subdued">{productImageCount} images optimized</s-text>
                            </s-stack>
                          </s-stack>
                          <s-stack direction="inline" gap="small-200" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const productSelectedImages = product.images.filter(img => selectedImages.has(img.id)).map(img => img.id);
                              return productSelectedImages.length > 0 && (
                                <>
                                  <s-button size="small" variant="secondary" tone="critical" onClick={() => handleProductRevertClick(product, productSelectedImages)} disabled={reverting === product.productId} loading={reverting === product.productId}>
                                    Revert ({productSelectedImages.length})
                                  </s-button>
                                  <s-button size="small" variant="secondary" commandFor={`reopt-${product.productId}`}>Re-optimize ({productSelectedImages.length})</s-button>
                                  <s-menu id={`reopt-${product.productId}`}>
                                    {optimizationTypes.map((type) => (
                                      <s-button key={type.key} onClick={() => handleProductReoptimize(product, type.key)} disabled={reoptimizing === product.productId} loading={reoptimizing === product.productId}>{type.title}</s-button>
                                    ))}
                                  </s-menu>
                                </>
                              );
                            })()}
                            <s-button variant="tertiary" icon="menu-vertical" commandFor={`actions-${product.productId}`} />
                            <s-menu id={`actions-${product.productId}`}>
                              <s-button onClick={() => {
                                let id = product.productGraphqlId;
                                if (typeof id === 'string' && id.startsWith('gid://')) id = id.split('/').pop();
                                window.open(`https://${shopDomain}/admin/products/${id}`, '_blank');
                              }} icon="product">View in Admin</s-button>
                              <s-button onClick={() => window.open(`https://${shopDomain}/products/${product.productHandle}`, '_blank')} icon="external">View in Storefront</s-button>
                            </s-menu>
                          </s-stack>
                        </s-grid>
                      </div>

                      {isExpanded && (
                        <div className="image-management-nested-images">
                          <s-stack gap="small-100">
                            {product.images.map((image) => (
                              <ImageRow key={image.id} image={image} isSelected={selectedImages.has(image.id)} selectAllStore={selectAllStore} onCompare={handleOpenCompare} />
                            ))}
                          </s-stack>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <PaginationFooter pageInfo={pageInfo} onPrevious={goToPreviousPage} onNext={goToNextPage} loading={fetching} itemName="products" />
            </div>
          )}

          {!fetching && !searching && products.length === 0 && (
            <EmptyState
              icon={debouncedSearchQuery.trim() ? '🔍' : '🖼️'}
              heading={debouncedSearchQuery.trim() ? 'No results found' : 'No optimized products yet'}
              message={debouncedSearchQuery.trim()
                ? `No products match "${debouncedSearchQuery.trim()}". Try a different search term.`
                : 'Optimized products appear here once you run your first optimization. Head to the Unoptimized tab to get started.'}
              actionContent={debouncedSearchQuery.trim() ? 'Clear Search' : (onGoToUnoptimized ? 'Go to Unoptimized' : undefined)}
              onAction={debouncedSearchQuery.trim() ? clearSearch : onGoToUnoptimized}
              actionVariant={debouncedSearchQuery.trim() ? 'secondary' : 'primary'}
            />
          )}
        </s-stack>
      </s-section>
      <CompareModal data={compareImage} onClose={() => setCompareImage(null)} />
    </>
  );
}

export default OptimizedImagesManagement;